<!--
 * @Author: Tahiti
 * @Date: 2025-12-17
 * @Description: 特效模板轮播组件
 * @FilePath: /weryai-web/components/Effects/EffectsCarousel.vue
-->
<template>
    <InfiniteCarousel
        :items="carouselData"
        :autoplay-interval="5000"
        :get-item-key="item => item.video || item.cover"
        :enable-snap="true"
        @item-click="handleItemClick"
    >
        <template #default="{ item }">
            <div class="w-full">
                <div
                    class="w-full aspect-[16/9] md:w-[560px] md:h-[315px] md:aspect-auto relative overflow-hidden rounded-[20px] group cursor-pointer"
                >
                    <!-- 封面图片：视频未加载完成时显示 -->
                    <img
                        v-if="
                            type === 'VIDEO' &&
                            item.cover &&
                            (!item.video || !videoLoadedMap[item.video])
                        "
                        :src="item.cover"
                        :alt="item.desc"
                        class="absolute inset-0 size-full object-cover group-hover:scale-110 rounded-[20px] transition-all duration-300"
                    />
                    <!-- 视频类型：加载完成后显示 -->
                    <video
                        v-if="type === 'VIDEO' && item.video"
                        v-show="videoLoadedMap[item.video]"
                        class="absolute inset-0 size-full object-cover group-hover:scale-110 rounded-[20px] transition-all duration-300"
                        :src="item.video"
                        :poster="getResourceThumbnail(item.video)"
                        loop
                        autoplay
                        muted
                        playsinline
                        webkit-playsinline
                        preload="metadata"
                        crossorigin="anonymous"
                        @canplay="handleVideoLoaded(item.video)"
                    ></video>
                    <!-- 图片类型（非视频类型时） -->
                    <template v-else-if="type !== 'VIDEO' && item.cover">
                        <!-- thumbnail -->
                        <img
                            :src="
                                getResourceThumbnail(item.cover) || item.cover
                            "
                            :alt="item.desc"
                            class="absolute inset-0 size-full object-cover group-hover:scale-110 rounded-[20px] transition-all duration-300"
                            loading="lazy"
                            decoding="async"
                            :style="{
                                opacity:
                                    !isImageLoaded(item) &&
                                    !isImageThumbError(item)
                                        ? 1
                                        : 0,
                            }"
                            @error="markImageThumbError(item)"
                        />
                        <!-- origin -->
                        <img
                            :src="item.cover"
                            :alt="item.desc"
                            class="absolute inset-0 size-full object-cover group-hover:scale-110 rounded-[20px] transition-all duration-300"
                            loading="lazy"
                            decoding="async"
                            :style="{
                                opacity:
                                    (isImageLoaded(item) ||
                                        isImageThumbError(item)) &&
                                    !isImageOriginError(item)
                                        ? 1
                                        : 0,
                            }"
                            @load="markImageLoaded(item)"
                            @error="markImageOriginError(item)"
                        />
                    </template>
                    <img
                        v-if="item.mask"
                        :src="item.mask"
                        alt="title"
                        class="absolute inset-0 size-full object-cover rounded-[20px] transition-all duration-300"
                    />
                    <!-- Use This Template 按钮：hover 时显示 -->
                    <button
                        class="absolute bottom-4 left-1/2 -translate-x-1/2 z-[2] py-2 px-6 bg-[#99F768] text-[#000] text-sm font-normal leading-normal rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
                        @click.stop="handleUseTemplate(item)"
                    >
                        {{ t('menus.useThisTemplate') }}
                    </button>
                </div>

                <div
                    class="w-full cursor-pointer flex-1 md:max-w-[560px] mt-5"
                >
                    <p class="carousel-title truncate">
                        {{ item.desc }}
                    </p>
                </div>
            </div>
        </template>
    </InfiniteCarousel>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from '~/composables/useI18n'
import InfiniteCarousel from '~/components/ui/InfiniteCarousel.vue'
import { getResourceThumbnail } from '~/utils/common'

const { t } = useI18n()

// Props：支持传入自定义数据
const props = defineProps<{
    items?: any[]
    type?: 'IMAGE' | 'VIDEO'
}>()

const type = computed(() => props.type || 'VIDEO')

const emit = defineEmits<{
    useTemplate: [item: any]
    itemClick: [item: any]
}>()

// 使用传入的数据，不使用默认数据
const carouselData = computed(() => {
    return props.items || []
})

// ===== 图片渐进加载（thumbnail -> origin，保证不闪）=====
const imageLoadedMap = ref<Record<string, boolean>>({})
const imageThumbErrorMap = ref<Record<string, boolean>>({})
const imageOriginErrorMap = ref<Record<string, boolean>>({})

const getImageKey = (item: any) => String(item?.cover || item?.desc || '')

const isImageLoaded = (item: any) => {
    const key = getImageKey(item)
    return key ? !!imageLoadedMap.value[key] : false
}

const isImageThumbError = (item: any) => {
    const key = getImageKey(item)
    return key ? !!imageThumbErrorMap.value[key] : false
}

const isImageOriginError = (item: any) => {
    const key = getImageKey(item)
    return key ? !!imageOriginErrorMap.value[key] : false
}

const markImageThumbError = (item: any) => {
    const key = getImageKey(item)
    if (!key) return
    imageThumbErrorMap.value[key] = true
}

const markImageOriginError = (item: any) => {
    const key = getImageKey(item)
    if (!key) return
    imageOriginErrorMap.value[key] = true
    imageLoadedMap.value[key] = false
}

const markImageLoaded = (item: any) => {
    const key = getImageKey(item)
    if (!key) return
    imageLoadedMap.value[key] = true
    imageOriginErrorMap.value[key] = false
}

/**
 * 核心修复点：不要只依赖 DOM @load（SSR/缓存时机下可能丢失）
 * 用 new Image() 主动预加载原图，确保一定能触发回调/complete 检测。
 */
const preloadCarouselImage = (item: any) => {
    if (!import.meta.client) return
    if (type.value === 'VIDEO') return
    const key = getImageKey(item)
    const src = item?.cover
    if (!key || !src) return
    if (imageLoadedMap.value[key]) return

    const img = new Image()
    img.src = src

    if (img.complete && img.naturalWidth > 0) {
        imageLoadedMap.value[key] = true
        imageOriginErrorMap.value[key] = false
        return
    }

    img.onload = () => {
        imageLoadedMap.value[key] = true
        imageOriginErrorMap.value[key] = false
    }
    img.onerror = () => {
        imageLoadedMap.value[key] = false
        imageOriginErrorMap.value[key] = true
    }
}

// 视频加载状态
const videoLoadedMap = ref<Record<string, boolean>>({})

const handleVideoLoaded = (videoUrl: string) => {
    if (videoUrl) {
        videoLoadedMap.value[videoUrl] = true
    }
}

// 当轮播数据变化时，重置加载状态
watch(
    () => props.items,
    () => {
        videoLoadedMap.value = {}
        imageLoadedMap.value = {}
        imageThumbErrorMap.value = {}
        imageOriginErrorMap.value = {}
        carouselData.value.forEach(preloadCarouselImage)
    },
    { deep: true }
)

onMounted(() => {
    carouselData.value.forEach(preloadCarouselImage)
})

// 点击卡片
const handleItemClick = (item: any) => {
    emit('itemClick', item)
}

// 点击 "Use This Template" 按钮
const handleUseTemplate = (item: any) => {
    emit('useTemplate', item)
}
</script>

<style lang="less" scoped>
.carousel-title {
    color: #fff;
    font-family: Roboto;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;

    &:hover {
        color: var(--el-menu-active-color);
    }
}
</style>
